/**
 * Id spaces, made visible to the compiler.
 *
 * THE PROBLEM THESE SOLVE
 * ----------------------
 * The column name `investor_id` means two different things depending on the
 * table:
 *
 *   auth.users(id)          commitments, data_room_access, dd_collaborators,
 *                           dd_note_reactions, dd_reports, decision_signals,
 *                           portfolio_companies, submission_links,
 *                           submissions, thesis_profiles
 *
 *   investor_profiles(id)   conversations, data_room_access_requests,
 *                           dealflow, investment_history
 *
 * Both are `uuid` in Postgres and `string` in TypeScript, so NOTHING could tell
 * them apart — not the compiler, not RLS, not a code review. Passing the wrong
 * one produces no error at all: the filter matches nothing, or the write is
 * silently rejected by a policy. It has shipped twice in a fortnight:
 *
 *   2026-07-30  every portfolio_companies write silently rejected
 *   2026-07-31  /notifications empty for every user of both personas, with a
 *               badge that could never clear
 *
 * Neither produced an error. Both were found by a human looking at a device.
 *
 * WHY BRANDS RATHER THAN A NAMING CONVENTION
 * ------------------------------------------
 * A convention is a thing people remember; a brand is a thing the compiler
 * enforces. `AuthUserId` and `InvestorProfileId` are both strings at runtime —
 * zero cost, no wrapper objects — but they are structurally incompatible at
 * compile time, so a mismatch is a build error rather than an empty screen.
 *
 * The authoritative split lives in `.planning/id-space-map.txt`, GENERATED from
 * the schema's foreign keys by `scripts/ci/generate-id-space-map.sh`, so these
 * brands cannot quietly drift from the database.
 *
 * `startup_id` is unambiguous (always startup_profiles) and `user_id` always
 * resolves to the auth user id — `profiles.id` is itself FK'd to
 * `auth.users(id)`. `StartupId` exists for symmetry and readability rather than
 * to resolve a conflict.
 *
 * HOW TO USE
 * ----------
 * Brand at the ORIGIN, not at every call site: `useAuth()` yields an
 * `AuthUserId`, `investorProfile.id` an `InvestorProfileId`, and everything
 * downstream inherits it. A function that takes one should say which:
 *
 *   useTrackRecord(userId: AuthUserId | undefined)
 *   useConversations(investorId: InvestorProfileId | undefined)
 *
 * Reach for `asAuthUserId()` only at a genuine boundary — a route param, an
 * RPC result, a value crossing in from untyped code — and treat each use as a
 * claim you are making about which space the value came from.
 */

declare const idSpace: unique symbol;

/** `auth.users(id)`. Also what `profiles.id` holds, and what `auth.uid()` returns. */
export type AuthUserId = string & { readonly [idSpace]: 'auth.users' };

/** `investor_profiles(id)`. NOT the investor's auth user id. */
export type InvestorProfileId = string & { readonly [idSpace]: 'investor_profiles' };

/** `startup_profiles(id)`. */
export type StartupId = string & { readonly [idSpace]: 'startup_profiles' };

/** Any of the branded id types — for helpers that genuinely do not care. */
export type AnyId = AuthUserId | InvestorProfileId | StartupId;

// ---------------------------------------------------------------------------
// Boundary constructors
// ---------------------------------------------------------------------------
//
// Each of these is an ASSERTION, not a conversion — there is nothing to check
// at runtime, both spaces being uuids. Use them where a value genuinely enters
// the typed world, and let the brand flow from there.

/** Assert a value is an `auth.users(id)`. */
export function asAuthUserId(id: string): AuthUserId;
export function asAuthUserId(id: string | undefined): AuthUserId | undefined;
export function asAuthUserId(id: string | undefined): AuthUserId | undefined {
  return id as AuthUserId | undefined;
}

/** Assert a value is an `investor_profiles(id)`. */
export function asInvestorProfileId(id: string): InvestorProfileId;
export function asInvestorProfileId(id: string | undefined): InvestorProfileId | undefined;
export function asInvestorProfileId(id: string | undefined): InvestorProfileId | undefined {
  return id as InvestorProfileId | undefined;
}

/** Assert a value is a `startup_profiles(id)`. */
export function asStartupId(id: string): StartupId;
export function asStartupId(id: string | undefined): StartupId | undefined;
export function asStartupId(id: string | undefined): StartupId | undefined {
  return id as StartupId | undefined;
}
